
import { useState, useEffect, createContext, useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface User {
  name: string;
  email: string;
  id?: string;
}

export type AuthContextType = {
  user: User | null;
  isAdmin: boolean;
  userRole: 'user' | 'admin';
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (userData?: User, isAdmin?: boolean) => void;
  logout: () => void;
  register: (email: string, password: string, name: string) => Promise<{success: boolean, message: string, userData?: User}>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<'user' | 'admin'>('user');

  useEffect(() => {
    let mounted = true;
    
    const checkAuth = async () => {
      try {
        console.log("AuthContext: Checking authentication status...");
        
        // Check localStorage first
        const authStatus = localStorage.getItem('isAuthenticated');
        const adminStatus = localStorage.getItem('isAdmin');
        const userDataStr = localStorage.getItem('userData');
        
        if (authStatus === 'true') {
          console.log("Found localStorage auth, restoring session");
          setIsAuthenticated(true);
          
          if (adminStatus === 'true') {
            setIsAdmin(true);
            setUserRole('admin');
          }
          
          if (userDataStr) {
            setUser(JSON.parse(userDataStr));
          }
        }
        
        // Check Supabase session
        const { data: sessionData } = await supabase.auth.getSession();
        const session = sessionData?.session;
        
        if (!mounted) return;
        
        if (session) {
          console.log('Supabase session found, user is authenticated');
          setIsAuthenticated(true);
          
          const userData = {
            name: session.user?.user_metadata?.name || session.user?.email?.split('@')[0] || '',
            email: session.user?.email || '',
            id: session.user?.id
          };
          
          setUser(userData);
          localStorage.setItem('userData', JSON.stringify(userData));
          localStorage.setItem('isAuthenticated', 'true');
          
          // Check admin status
          try {
            const { data: isAdminData, error: isAdminError } = await supabase.rpc('is_admin_user');
            
            if (!mounted) return;
            
            if (!isAdminError && isAdminData !== null) {
              console.log("User admin status:", isAdminData);
              setIsAdmin(isAdminData);
              setUserRole(isAdminData ? 'admin' : 'user');
              localStorage.setItem('isAdmin', isAdminData ? 'true' : 'false');
            } else {
              setIsAdmin(false);
              setUserRole('user');
              localStorage.setItem('isAdmin', 'false');
            }
          } catch (adminCheckError) {
            console.error("Error checking admin status:", adminCheckError);
            setIsAdmin(false);
            setUserRole('user');
            localStorage.setItem('isAdmin', 'false');
          }
        }
      } catch (err) {
        console.error("Error checking auth:", err);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };
    
    checkAuth();
    
    // Auth state change listener
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      
      console.log("Auth state changed:", event);
      
      if (event === 'SIGNED_IN' && session) {
        const userData = {
          name: session.user?.user_metadata?.name || session.user?.email?.split('@')[0] || '',
          email: session.user?.email || '',
          id: session.user?.id
        };
        
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('userData', JSON.stringify(userData));
        localStorage.setItem('isAuthenticated', 'true');
      } else if (event === 'SIGNED_OUT') {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('isAdmin');
        localStorage.removeItem('userData');
        setIsAuthenticated(false);
        setIsAdmin(false);
        setUserRole('user');
        setUser(null);
      }
    });
    
    return () => {
      mounted = false;
      authListener?.subscription.unsubscribe();
    };
  }, []);

  const login = (userData?: User, isAdminFlag = false) => {
    console.log('Login called with:', { userData, isAdminFlag });
    
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('isAdmin', isAdminFlag ? 'true' : 'false');
    
    if (userData) {
      localStorage.setItem('userData', JSON.stringify(userData));
      setUser(userData);
    }
    
    setIsAuthenticated(true);
    setIsAdmin(isAdminFlag);
    setUserRole(isAdminFlag ? 'admin' : 'user');
    console.log('Login successful. Admin:', isAdminFlag, 'User:', userData);
  };

  const logout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('userData');
    setIsAuthenticated(false);
    setIsAdmin(false);
    setUserRole('user');
    setUser(null);
    console.log('Logout successful');
    
    supabase.auth.signOut().catch(err => {
      console.error("Error signing out from Supabase:", err);
    });
  };

  const register = async (email: string, password: string, name: string) => {
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            is_approved: false,
            needs_onboarding: true
          }
        }
      });
      
      if (authError) {
        console.error("Registration error:", authError);
        return { 
          success: false, 
          message: authError.message || "Registration failed. Please try again." 
        };
      }
      
      const userData = {
        name,
        email,
        id: authData?.user?.id
      };
      
      localStorage.setItem('tempUserData', JSON.stringify(userData));
      
      return { 
        success: true, 
        message: "Registration successful. Please wait for admin approval.", 
        userData 
      };
    } catch (error: any) {
      console.error("Unexpected registration error:", error);
      return { 
        success: false, 
        message: error.message || "An unexpected error occurred during registration." 
      };
    }
  };

  const value = {
    user,
    isAdmin,
    userRole,
    isAuthenticated,
    isLoading,
    login,
    logout,
    register
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const ProtectedRoute = ({ 
  children, 
  requiredRole = "user" 
}: { 
  children: React.ReactNode | (({ isAdmin }: { isAdmin: boolean }) => React.ReactNode), 
  requiredRole?: "user" | "admin" 
}) => {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#1A1A1A] text-white">Laden...</div>;
  }

  if (!isAuthenticated) {
    console.log('Not authenticated, redirecting to login');
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (requiredRole === "admin" && !isAdmin) {
    console.log('Not admin, redirecting to home');
    return <Navigate to="/" replace />;
  }
  
  if (typeof children === 'function') {
    return <>{children({ isAdmin })}</>;
  }
  
  return <>{children}</>;
};
